import { z } from "zod";

// Type-only: the status vocabulary is read in a `typeof` position and nothing
// here needs it at runtime. The console imports this module to re-derive a row
// an older backend did not stamp, so it must not drag the generated enum table
// into the browser bundle.
import type { DbEnum } from "../../../generated/prisma-enums";
import { enumReadSchema } from "../../core/enum-read.schema";
import { isRoutineDriftCountsLine } from "./drift-counts-line";
import { readForcedConvergeReason } from "./forced-converge-reason";

/**
 * What the operator fleet roster should SAY about one tenant cluster.
 *
 * DERIVED, never stored. There is no column: `VibeTenantClusterStatus` is a
 * single database status and `DEGRADED` carries three different facts at once,
 * so the discriminator has to be computed at read time from the row the reader
 * already has.
 *
 * WHY THIS EXISTS. `DEGRADED` means "up, but not in its desired state". The
 * reconcile sweep writes it on ANY non-converged Pulumi preview, so a deploy
 * whose program changes what every tenant's stacks would apply drifts the WHOLE
 * fleet at once. On 2026-08-15 all six tenants read `Degraded` at once while every one of them
 * was serving, `gitHostStatus: HEALTHY`, `telemetryStatus: HEALTHY`, self-healed
 * on the next converge. The entity's own `isServing()` docblock already says a
 * raw `status === HEALTHY` check is a bug "because it treats a benign pending
 * config diff as a dead cluster"; the roster was that bug one layer up, showing
 * a benign pending diff and a dead cluster as the same orange caution chip.
 *
 * THREE STATES, NOT TWO SEVERITIES OF ONE. A pending config diff, a fault, and
 * "we cannot tell" are different answers, and the third is not a quieter shade
 * of the second:
 *
 *   - `PENDING_CONFIG_DIFF` — the cluster is serving and the only thing standing
 *     between it and converged is a set of routine pending changes. Neutral.
 *   - `DEGRADED` — a fault. Unchanged in meaning and unchanged in loudness; a
 *     destructive pending change, a missing stack, a failed converge and every
 *     reason this module cannot read all land here.
 *   - `DEGRADED_UNCLASSIFIED` — the row claims degraded and nothing on it says
 *     why, or the loop degraded it precisely because it could not observe the
 *     cluster. Neither green nor red: it is an inability, and it must not wear
 *     the colour of a measurement that was taken.
 *
 * Every other status passes through unchanged, so the roster keeps one axis and
 * a `Record<VibeTenantClusterRosterState, …>` in the console fails the typecheck
 * until each of the three has a treatment.
 *
 * THE DEFAULT IS THE LOUD ONE, AND THAT DIRECTION IS THE WHOLE SAFETY ARGUMENT.
 * Today every `DEGRADED` row is a caution chip. This module only ever SUBTRACTS
 * that chip, and only on a WHOLE reading — every part of the reason recognised,
 * every part routine. A reason it cannot parse keeps the chip it has always had,
 * so a cluster that sat `DEGRADED` on one un-applied ingress diff for 17.5 hours
 * still shouts, and an `VpcLimitExceeded` provision failure still shouts.
 */
export const VIBE_TENANT_CLUSTER_ROSTER_STATE_VALUES = [
  /// Passed through from `status`.
  "PROVISIONING",
  "HEALTHY",
  "UPDATING",
  /// `DEGRADED` + a drift-preview summary whose every pending change is routine
  /// (create / update / adopt, or a raft roll a converge will start). The
  /// fleet-wide post-deploy case. Informational.
  "PENDING_CONFIG_DIFF",
  /// `DEGRADED` + evidence of an actual fault, or a reason this module declines
  /// to read as benign. The chip an operator must act on.
  "DEGRADED",
  /// `DEGRADED` + no evidence at all: a null reason, or the unobservable escape
  /// hatch. UNMEASURED — not green, not red.
  "DEGRADED_UNCLASSIFIED",
  /// Passed through from `status`.
  "DISABLING",
  "DISABLED_RETAINED",
  "DESTROYING",
  "DESTROYED"
] as const;

export type VibeTenantClusterRosterState = (typeof VIBE_TENANT_CLUSTER_ROSTER_STATE_VALUES)[number];

export const VibeTenantClusterRosterStateSchema = z.enum(VIBE_TENANT_CLUSTER_ROSTER_STATE_VALUES);

/**
 * The roster state as a READER parses it. The backend derives it and grows it —
 * `PENDING_CONFIG_DIFF` and `DEGRADED_UNCLASSIFIED` were both added after the
 * roster shipped — so a state a newer backend derives reads as the server's own
 * word instead of failing the whole roster read.
 */
export const VibeTenantClusterRosterStateReadSchema = enumReadSchema(
  VIBE_TENANT_CLUSTER_ROSTER_STATE_VALUES
);

type VibeTenantClusterStatusLiteral = (typeof DbEnum.VIBE_TENANT_CLUSTER_STATUS_VALUES)[number];

/**
 * The `statusReason` the reconcile loop writes when every one of a cluster's
 * stacks reads as "nothing is out there". Exported so the producer and this
 * classifier cannot drift: a literal spelled twice is a literal that will one
 * day be spelled two ways, and the failure would be silent in the direction
 * that matters (a total wipe rendering as a benign diff).
 */
export const TENANT_CLUSTER_REASON_ALL_STACKS_ABSENT = "all stacks absent";

/**
 * Opening of a `statusReason` stating that the loop degraded the cluster
 * because it could not OBSERVE it. Nothing writes it any more — the loop never
 * acts on a cluster it cannot see — so it classifies the rows that still carry
 * it until the loop's next refreshed observation of each replaces the reason.
 *
 * debt: kept only to classify rows written before the loop stopped degrading
 *       unobservable clusters.
 *       Upgrade trigger: no VibeTenantCluster row in any environment has a
 *       statusReason starting with this prefix — then delete it and its branch.
 */
export const TENANT_CLUSTER_REASON_UNOBSERVABLE_PREFIX = "unobservable for ";

/**
 * Whether a `DEGRADED` cluster's reason is provably nothing but routine pending
 * changes.
 *
 * Reads the FIRST line only, and that is not a shortcut: the first line carries
 * the counts, which are the verdict, and the indented lines below it are an
 * elaboration that `renderDriftSummary` may truncate under a character budget. A
 * classifier reading a possibly-truncated elaboration could call a summary whole
 * when it was not.
 *
 * The line's grammar is `drift-counts-line.ts`'s, the same vocabulary the drift
 * fold formats through, so a clause the producer can emit is a clause this reads.
 */
function isRoutinePendingDiff(reason: string): boolean {
  const [countsLine] = reason.split("\n");
  return countsLine !== undefined && isRoutineDriftCountsLine(countsLine);
}

/**
 * Resolve what the roster should render for one cluster row.
 *
 * Pure and total over `VibeTenantClusterStatus`; the switch is exhaustive, so a
 * new database status is a compile error here rather than a row that quietly
 * inherits someone else's chip.
 *
 * `statusReason` is only ever consulted on `DEGRADED`. Every other status
 * already means one thing.
 */
export function resolveVibeTenantClusterRosterState(input: {
  status: VibeTenantClusterStatusLiteral;
  statusReason: string | null;
}): VibeTenantClusterRosterState {
  const { status, statusReason } = input;

  switch (status) {
    case "PROVISIONING":
    case "HEALTHY":
    case "UPDATING":
    case "DISABLING":
    case "DISABLED_RETAINED":
    case "DESTROYING":
    case "DESTROYED":
      return status;
    case "DEGRADED": {
      // The row asserts a fault and carries no evidence for it. That is an
      // inability to say, not a diagnosis, and rendering it as either a fault or
      // a benign diff would be inventing the half that is missing.
      if (statusReason === null || statusReason.trim().length === 0) {
        return "DEGRADED_UNCLASSIFIED";
      }
      // An operator forced a converge. That is pending work somebody asked for,
      // so it is judged by what it wraps (`forced-converge-reason.ts`): nothing
      // previous is routine, a previous reason is classified as if it still stood
      // — a fault stays a fault — and a wrap that does not close is not read.
      const forced = readForcedConvergeReason(statusReason);
      if (forced !== null) {
        switch (forced.kind) {
          case "fresh":
            return "PENDING_CONFIG_DIFF";
          case "unread":
            return "DEGRADED";
          case "over":
            return resolveVibeTenantClusterRosterState({
              status: "DEGRADED",
              statusReason: forced.previous
            });
        }
      }
      // The loop degraded this cluster BECAUSE it could not observe it. The one
      // reason string in the system that states its own unmeasuredness.
      if (statusReason.startsWith(TENANT_CLUSTER_REASON_UNOBSERVABLE_PREFIX)) {
        return "DEGRADED_UNCLASSIFIED";
      }
      // Every live resource gone. The severe case, and it is a fixed literal
      // rather than a preview summary, so it never reaches the diff grammar.
      if (statusReason === TENANT_CLUSTER_REASON_ALL_STACKS_ABSENT) return "DEGRADED";
      return isRoutinePendingDiff(statusReason) ? "PENDING_CONFIG_DIFF" : "DEGRADED";
    }
    default: {
      const exhaustive: never = status;
      void exhaustive;
      return "DEGRADED_UNCLASSIFIED";
    }
  }
}
